import React, { useEffect, useMemo, useState } from 'react';
import {
  BlackBackground,
  CloseWrapper,
  SearchWrapper,
  StickyHeader,
  TabsWrapper,
} from './SidewideSearch.styles';
import { SearchField, Tabs, TextButton } from '@oneaudi/unified-web-components';
import useFetchSitewideSearch from '../../../hooks/useFetchSitewideSearch';
import VehicleList from './VehicleList';
import { useSpring, animated } from 'react-spring';
import NoResults from './NoResults';
import Spinner from './LoadingSpinner';
import type { InventoryGroup } from '../../../types';
import type { LocaleServiceV1, DealerContextServiceV1 } from '../../feature-service-types';
import VehicleCard from './VehicleCard';

interface SitewideSearchProps {
  setSidewideSearchOpen: (arg: boolean) => void;
  localeService?: LocaleServiceV1;
  dealerContextService: DealerContextServiceV1;
  sortBy: string;
  showPrice: boolean;
}

const SitewideSearch: React.FC<SitewideSearchProps> = ({
  setSidewideSearchOpen,
  localeService,
  dealerContextService,
  sortBy,
  showPrice,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [isClosing, setIsClosing] = useState(false);

  const { listOfVehicles, singleVehicle, warnings, searchVehicles, error, loading } =
    useFetchSitewideSearch();

  const isSingleVehicle = !listOfVehicles && !!singleVehicle;

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  //------------------- Dealer Context ---------------------//
  const [dealer, setDealer] = useState(() => dealerContextService?.getSelectedDealer());

  useEffect(() => {
    if (!dealerContextService) return;
    const unsubscribe = dealerContextService.subscribe((dealerId) => {
      setDealer(dealerId);
    });
    return () => {
      unsubscribe();
    };
  }, [dealerContextService]);

  //------------------- Animation ---------------------//
  const slideIn = useSpring({
    from: { transform: 'translateX(100%)' },
    to: { transform: isClosing ? 'translateX(100%)' : 'translateX(0%)' },
    config: { tension: 220, friction: 28 },
    onRest: () => {
      if (isClosing) setSidewideSearchOpen(false);
    },
  });

  const fadeIn = useSpring({
    from: { opacity: 0 },
    to: { opacity: isClosing ? 0 : 1 },
    config: { tension: 220, friction: 28 },
  });
  //-------------------------------------------------//

  const handleClose = () => {
    setIsClosing(true);
  };

  const market = useMemo(() => {
    if (localeService?.countryCode === 'US' || localeService?.countryCode === 'CA')
      return localeService?.countryCode.toLowerCase();
    return 'us';
  }, [localeService?.countryCode]);

  const language = useMemo(() => {
    const localeLanguage = localeService?.language.toLowerCase();
    if (localeLanguage === 'en' || localeLanguage === 'fr') return localeLanguage;
    return 'en';
  }, [localeService?.language]);

  const handleSearch = () => {
    if (!searchInput?.trim()) return;
    void searchVehicles({
      variables: {
        input: {
          searchQuery: searchInput,
          market: market,
          language: language,
          sort: sortBy ?? 'OLDEST_IN_LOT',
          dealerId: dealer?.id ?? '',
        },
      },
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      (event.target as HTMLElement).blur();
      handleSearch();
    }
  };

  const newVehicles = listOfVehicles?.find((toCheck) => toCheck.key === 'NEW');
  const usedVehicles = listOfVehicles?.find((toCheck) => toCheck.key === 'PREOWNED');
  const cpoVehicles = listOfVehicles?.find((toCheck) => toCheck.key === 'CPO');

  const foundNothing =
    !!warnings?.find((toCheck) => toCheck.code === 'NO_FILTERS_EXTRACTED') ||
    (!newVehicles?.vehicles.length &&
      !usedVehicles?.vehicles.length &&
      !cpoVehicles?.vehicles.length &&
      listOfVehicles);

  useEffect(() => {
    if (newVehicles?.vehicles.length) setActiveTab(0);
    if (!newVehicles?.vehicles.length && usedVehicles?.vehicles.length) setActiveTab(1);
    if (!newVehicles?.vehicles.length && !usedVehicles?.vehicles.length) setActiveTab(2);
  }, [newVehicles, usedVehicles]);

  const isEmptyObject = (listOfVehicles: InventoryGroup | undefined) => {
    if (!listOfVehicles || !listOfVehicles?.vehicles.length) return true;
    return Object.keys(listOfVehicles).length === 0;
  };

  const AnimatedBlackBackground = animated(BlackBackground);
  const AnimatedSearchWrapper = animated(SearchWrapper);

  const TabsWithResults = useMemo(() => {
    return (
      <TabsWrapper>
        <Tabs
          id="sitewide-search-tabs"
          size="md"
          scrollButtons={{
            left: { ariaLabel: 'Scroll left' },
            right: { ariaLabel: 'Scroll right' },
          }}
          items={[
            {
              label: language === 'fr' ? 'Neuf' : 'New',
              active: activeTab === 0,
              disabled: isEmptyObject(newVehicles),
              onClick: () => {
                setActiveTab(0);
              },
              content: (
                <VehicleList
                  vehiclesData={newVehicles}
                  showPrice={showPrice}
                  type="NEW"
                  language={language}
                  market={market}
                />
              ),
            },
            {
              label: language === 'fr' ? 'D’occasion' : 'Pre-owned',
              active: activeTab === 1,
              disabled: isEmptyObject(usedVehicles),
              onClick: () => {
                setActiveTab(1);
              },
              content: (
                <VehicleList
                  vehiclesData={usedVehicles}
                  showPrice={showPrice}
                  type="USED"
                  language={language}
                  market={market}
                />
              ),
            },
            {
              label:
                language === 'fr'
                  ? 'Certifié :plus'
                  : `Certified ${market !== 'us' ? ':plus' : 'Pre-owned'}`,
              active: activeTab === 2,
              disabled: isEmptyObject(cpoVehicles),
              onClick: () => {
                setActiveTab(2);
              },
              content: (
                <VehicleList
                  vehiclesData={cpoVehicles}
                  showPrice={showPrice}
                  type="CPO"
                  language={language}
                  market={market}
                />
              ),
            },
          ]}
          onKeyNavigate={(newIndex) => {
            setActiveTab(newIndex);
          }}
        />
      </TabsWrapper>
    );
  }, [activeTab, cpoVehicles, newVehicles, showPrice, usedVehicles, language, market]);

  const RenderContent = useMemo(() => {
    if (foundNothing) return <NoResults language={language} market={market} />;
    if (isSingleVehicle) {
      const vehicleType = singleVehicle[0]?.stockType;
      return (
        <TabsWrapper>
          <VehicleCard vehicle={singleVehicle[0]} showPrice={showPrice} type={vehicleType} />
        </TabsWrapper>
      );
    }
    if (!listOfVehicles && !loading && error)
      return <NoResults isError language={language} market={market} />;
    if (!listOfVehicles && loading && !error) return <Spinner size={48} />;
    if (listOfVehicles && !loading && !error) return TabsWithResults;
    return null;
  }, [
    TabsWithResults,
    error,
    foundNothing,
    isSingleVehicle,
    listOfVehicles,
    loading,
    showPrice,
    singleVehicle,
    language,
    market,
  ]);

  return (
    <>
      <AnimatedBlackBackground style={fadeIn} />
      <AnimatedSearchWrapper style={slideIn}>
        <StickyHeader>
          <CloseWrapper>
            <TextButton
              aria-label="close button"
              type="button"
              variant="tertiary"
              onClick={handleClose}
            >
              {language === 'fr' ? 'Fermer' : 'Close'}
            </TextButton>
          </CloseWrapper>
          <SearchField
            className="className"
            clearButton={{
              'aria-label': language === 'fr' ? 'Effacer la recherche' : 'Clear the search query',
              onClick: () => {
                setSearchInput('');
              },
            }}
            data-testid="sitewide-search-search-field"
            hint={
              language === 'fr'
                ? 'Recherchez par marque, modèle, prix ou kilométrage. Appuyez sur Entrée pour afficher vos résultats.'
                : "Search by model, color, mileage, price, or lifestyle - e.g. 'sporty family SUV'. Press Enter to view results."
            }
            id="search-field"
            label={
              language === 'fr' ? 'Recherchez votre véhicule' : "Describe what you're looking for"
            }
            margin={[0, 0, '12px', 0]}
            onChange={(e) => {
              setSearchInput(e.target.value);
            }}
            onClick={() => {}}
            onKeyDown={handleKeyDown}
            value={searchInput}
          />
        </StickyHeader>
        {RenderContent}
      </AnimatedSearchWrapper>
    </>
  );
};

export default SitewideSearch;
